// Generated by Claude Code
import { State } from '#core/state/state-core.ts'

/**
 * Base for the upsert states whose drawer guards its close with an unsaved-changes
 * confirm: the subclass declares what counts as unsaved work in
 * {@link currentDirtySignature} and marks its clean points with
 * {@link captureDirtyBaseline}; the drawer reads {@link isDirty}.
 */
export abstract class DirtyTrackedState<TSelf extends DirtyTrackedState<TSelf>> extends State<TSelf> {
  /** Signature of the last clean point, null until one is captured. */
  private dirtyBaseline: string | null = null

  /**
   * Serializes the fields whose change means unsaved work. Draft-only inputs (a
   * half-filled "add" row the form discards itself) stay out.
   */
  protected abstract currentDirtySignature(): string

  /**
   * Returns to the no-baseline (clean) state until the next capture. Call at the start of
   * an init path: a reused drawer instance still holds the previous baseline, against which
   * the values an init path assigns before its loads settle would read as unsaved work.
   */
  clearDirtyBaseline(): void {
    this.reactTo.dirtyBaseline = null
  }

  /** Marks the current field values as the clean baseline {@link isDirty} compares against. */
  captureDirtyBaseline(): void {
    // Assigned reactively: the drawer already computed `isDirty` against the previous
    // baseline, so without a re-render it would keep reading that stale answer for the
    // rest of the session.
    this.reactTo.dirtyBaseline = this.currentDirtySignature()
  }

  /**
   * True when the tracked fields differ from the last captured baseline. Never dirty before a
   * baseline exists, so a form whose load (or reset) is still in flight holds no unsaved work.
   */
  get isDirty(): boolean {
    return this.dirtyBaseline !== null && this.currentDirtySignature() !== this.dirtyBaseline
  }
}
